import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/api';
import {
  Card,
  Button,
  Space,
  Select,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  theme,
} from 'antd';
import {
  PlusOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import { PageHeader, PageContainer } from '../components/common';
import { PermissionButton } from '../components/common/PermissionButton';
import { useAuthStore } from '../stores/authStore';
import {
  getPurchaseOrderDisplayStatusView,
  type PurchaseOrderDisplayStatusView,
} from '../utils/purchaseOrderDisplayStatus';
import {
  calculatePurchaseOrderTaxIncludedSummary,
  formatPurchaseOrderMoney,
} from '../utils/purchaseOrderTaxIncludedSummary';

const { Title, Text } = Typography;
const { useToken } = theme;

interface PurchaseOrder {
  order: {
    id: string;
    companyId: string;
    displayId: string;
    supplierId: string;
    orderDate: string;
    expectedDeliveryDate?: string | null;
    actualDeliveryDate?: string | null;
    status: string;
    approvalRequired?: boolean | null;
    approvalStatus?: ApprovalStatus | null;
    approvalAssigneeId?: string | null;
    subtotal?: string | null;
    tax?: string | null;
    shippingCost?: string | null;
    totalAmount?: string | null;
    orderedBy: string;
    notes?: string | null;
    createdAt: string;
    updatedAt: string;
  };
  supplier: {
    id: string;
    name: string;
    displayId: string;
  };
  approvalAssignee?: {
    id: string;
    label: string;
    email?: string | null;
  } | null;
  items: Array<{
    item: {
      id: string;
      quantity: number;
      receivedQuantity: number;
      unitPrice: string;
      amount: string;
    };
    part: {
      id: string;
      name: string;
      displayId: string;
      unit?: string | null;
    };
  }>;
}

type OrderStatus = 'draft' | 'ordered' | 'partial' | 'delivered' | 'cancelled';
type ApprovalStatus = 'not_required' | 'draft' | 'pending' | 'approved' | 'rejected';

const approvalStatusView: Record<ApprovalStatus, { text: string; color: string }> = {
  not_required: { text: '承認不要', color: 'default' },
  draft: { text: '承認依頼前', color: 'default' },
  pending: { text: '承認待ち', color: 'warning' },
  approved: { text: '承認済み', color: 'success' },
  rejected: { text: '差し戻し', color: 'error' },
};

export function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useToken();
  const { user } = useAuthStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | undefined>();
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState<ApprovalStatus | undefined>();
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [pageSize] = useState(20);
  const legacyCategoryOrderId =
    searchParams.get('order') === 'category'
      ? searchParams.get('category') || undefined
      : undefined;

  useEffect(() => {
    if (!legacyCategoryOrderId) return;
    navigate(`/app/purchase-orders/category-orders/${encodeURIComponent(legacyCategoryOrderId)}`, {
      replace: true,
    });
  }, [legacyCategoryOrderId, navigate]);

  // 発注一覧取得
  const { data: ordersData, isLoading } = trpc.purchaseOrders.list.useQuery({
    page: currentPage,
    limit: pageSize,
    status: selectedStatus,
    approvalStatus: assignedToMe ? 'pending' : selectedApprovalStatus,
    assignedToMe,
  });

  // 部品一覧を取得（部品0件の場合の誘導表示に使用）
  const { data: partsData, isLoading: isLoadingParts } = trpc.parts.list.useQuery({
    page: 1,
    limit: 1,
  });

  const hasParts = (partsData?.pagination?.total ?? 0) > 0;

  const orders = useMemo<PurchaseOrder[]>(
    () => (Array.isArray(ordersData?.items) ? (ordersData.items as unknown as PurchaseOrder[]) : []),
    [ordersData]
  );
  const total = ordersData?.pagination?.total || 0;

  // ステータス別カウント
  const draftCount = orders.filter(o => o.order.status === 'draft').length;
  const orderedCount = orders.filter(o => o.order.status === 'ordered').length;
  const deliveredCount = orders.filter(o => o.order.status === 'delivered').length;
  const pendingApprovalCount = orders.filter(o =>
    o.order.approvalRequired && o.order.approvalStatus === 'pending'
  ).length;

  // ステータスタグ
  const getStatusTag = (order: PurchaseOrder['order']) => {
    const iconMap: Record<PurchaseOrderDisplayStatusView['icon'], ReactNode> = {
      clock: <ClockCircleOutlined aria-hidden="true" />,
      shoppingCart: <ShoppingCartOutlined aria-hidden="true" />,
      checkCircle: <CheckCircleOutlined aria-hidden="true" />,
      exclamationCircle: <ExclamationCircleOutlined aria-hidden="true" />,
    };
    const config = getPurchaseOrderDisplayStatusView(order);

    return (
      <Tag icon={iconMap[config.icon]} color={config.color}>
        {config.text}
      </Tag>
    );
  };

  const getApprovalTag = (status?: ApprovalStatus | null, required?: boolean | null) => {
    const normalizedStatus = required ? status ?? 'draft' : 'not_required';
    const config = approvalStatusView[normalizedStatus] ?? approvalStatusView.not_required;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // テーブルカラム定義
  const columns: ResponsiveColumnType<PurchaseOrder>[] = [
    {
      title: '業者名',
      dataIndex: ['supplier', 'name'],
      key: 'supplierName',
      width: 200,
      fixed: 'left',
      mobileRole: 'title',
      mobilePriority: 1,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: '発注物',
      dataIndex: 'items',
      key: 'items',
      width: 300,
      mobileRole: 'description',
      mobilePriority: 3,
      render: (items: PurchaseOrder['items']) => {
        if (!items || items.length === 0) return '-';

        const itemTexts = items.map((item) =>
          `${item.part.name} (${item.item.quantity}${item.part.unit || '個'})`
        );

        if (items.length > 3) {
          return (
            <div>
              {itemTexts.slice(0, 3).join(', ')}
              <Text type="secondary"> 他{items.length - 3}件</Text>
            </div>
          );
        }

        return itemTexts.join(', ');
      },
    },
    {
      title: 'ステータス',
      dataIndex: ['order', 'status'],
      key: 'status',
      width: 120,
      mobileRole: 'tag',
      mobilePriority: 2,
      render: (_, record) => getStatusTag(record.order),
    },
    {
      title: '承認',
      key: 'approval',
      width: 160,
      mobileRole: 'tag',
      mobilePriority: 3,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {getApprovalTag(record.order.approvalStatus, record.order.approvalRequired)}
          {record.order.approvalRequired && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.approvalAssignee?.label ?? '承認先未設定'}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '発注日',
      dataIndex: ['order', 'orderDate'],
      key: 'orderDate',
      width: 120,
      mobileRole: 'description',
      mobilePriority: 4,
      render: (date) => new Date(date).toLocaleDateString('ja-JP'),
    },
    {
      title: '納品予定日',
      dataIndex: ['order', 'expectedDeliveryDate'],
      key: 'expectedDeliveryDate',
      width: 120,
      mobileRole: 'hidden',
      render: (date) => date ? new Date(date).toLocaleDateString('ja-JP') : '-',
    },
    {
      title: '合計金額(税込)',
      dataIndex: ['order', 'totalAmount'],
      key: 'totalAmount',
      width: 120,
      align: 'right',
      mobileRole: 'description',
      mobilePriority: 5,
      render: (_, record) => formatPurchaseOrderMoney(
        calculatePurchaseOrderTaxIncludedSummary(record.order, record.items).totalAmount,
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      mobileRole: 'hidden',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/purchase-orders/${record.order.id}`)}
        >
          詳細
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="発注管理"
        subtitle="部品・消耗品の発注履歴と納品状況を管理します"
        extra={
          <Space wrap>
            <Button
              icon={<AppstoreOutlined aria-hidden="true" />}
              size="large"
              onClick={() => navigate('/app/purchase-orders/category-orders')}
            >
              カテゴリ別 発注・消費・追加
            </Button>
            <PermissionButton
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => navigate('/app/purchase-orders/create')}
              requiredPermission="edit"
            >
              新規発注
            </PermissionButton>
          </Space>
        }
      />
      {/* 部品が0件の場合 - 部品登録への誘導バナー */}
      {!isLoadingParts && !hasParts && (
        <Card
          style={{
            marginBottom: 16,
            background: token.colorInfoBg,
            border: `1px solid ${token.colorInfoBorder}`,
          }}
        >
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <AppstoreOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
            </Col>
            <Col flex={1}>
              <Title level={5} style={{ marginBottom: 4 }}>
                まず部品・消耗品を登録しましょう
              </Title>
              <Text type="secondary">
                部品・消耗品を登録すると、以下の機能が利用できるようになります。
              </Text>
            </Col>
            <Col>
              <Button type="primary" onClick={() => navigate('/app/parts')}>
                部品・消耗品を登録する
              </Button>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <BarChartOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                  <div>
                    <Text strong>在庫管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      在庫数を把握し、不足を事前に検知
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <ShoppingCartOutlined style={{ fontSize: 20, color: token.colorWarning }} />
                  <div>
                    <Text strong>発注管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      発注履歴を記録し、コストを管理
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <HistoryOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                  <div>
                    <Text strong>消費管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      使用履歴を追跡し、消耗パターンを分析
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="下書き"
              value={draftCount}
              suffix="件"
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="発注済み"
              value={orderedCount}
              suffix="件"
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="納品済み"
              value={deliveredCount}
              suffix="件"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="承認待ち発注"
              value={pendingApprovalCount}
              suffix="件"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* フィルタ */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" wrap>
          <Select
            placeholder="ステータスで絞込"
            style={{ width: 200 }}
            value={selectedStatus}
            onChange={setSelectedStatus}
            allowClear
            options={[
              { label: '下書き', value: 'draft' },
              { label: '発注済み', value: 'ordered' },
              { label: '部分納品', value: 'partial' },
              { label: '納品済み', value: 'delivered' },
              { label: 'キャンセル', value: 'cancelled' },
            ]}
          />
          <Select
            placeholder="承認状態で絞込"
            style={{ width: 200 }}
            value={selectedApprovalStatus}
            onChange={(value) => {
              setSelectedApprovalStatus(value);
              setAssignedToMe(false);
              setCurrentPage(1);
            }}
            allowClear
            options={[
              { label: '承認不要', value: 'not_required' },
              { label: '承認依頼前', value: 'draft' },
              { label: '承認待ち', value: 'pending' },
              { label: '承認済み', value: 'approved' },
              { label: '差し戻し', value: 'rejected' },
            ]}
          />
          <Button
            type={assignedToMe ? 'primary' : 'default'}
            disabled={!user?.id}
            onClick={() => {
              const next = !assignedToMe;
              setAssignedToMe(next);
              setSelectedApprovalStatus(next ? 'pending' : undefined);
              setCurrentPage(1);
            }}
          >
            自分の承認待ち
          </Button>
          <Button
            onClick={() => {
              setSelectedStatus(undefined);
              setSelectedApprovalStatus(undefined);
              setAssignedToMe(false);
              setCurrentPage(1);
            }}
          >
            クリア
          </Button>
        </Space>
      </Card>

      {/* 発注一覧テーブル */}
      <Card>
        <ResponsiveTable<PurchaseOrder>
          columns={columns}
          dataSource={orders}
          loading={isLoading}
          rowKey={(record) => record.order.id}
          scrollX={1000}
          mobileCardConfig={{
            titleField: (record) => (
              <a
                onClick={() => navigate(`/app/purchase-orders/${record.order.id}`)}
                style={{ color: '#1890ff', fontWeight: 'bold', cursor: 'pointer' }}
              >
                {record.supplier.name}
              </a>
            ),
            onClick: (record) => navigate(`/app/purchase-orders/${record.order.id}`),
          }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: false,
            showTotal: (total) => `全 ${total} 件`,
            onChange: (page) => setCurrentPage(page),
          }}
        />
      </Card>
    </PageContainer>
  );
}
